import { useRef } from "react";
import QRCode from "react-qr-code";
import { Download, Printer, QrCode, ExternalLink, Copy, Radar, FileText } from "lucide-react";
import DashboardShell from "@/components/hola/DashboardShell";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { jsPDF } from "jspdf";

export default function QRPage() {
  return (
    <DashboardShell active="qr">
      {({ merchant }) => <QRBody merchant={merchant} />}
    </DashboardShell>
  );
}

function QRBody({ merchant }) {
  const svgRef = useRef(null);
  // QR UNIVERSAL: mismo código para todos los comercios de la red HOLA RADAR.
  // Al escanearlo, el usuario abre /scan y crea una sesión anónima que le
  // permite recibir ofertas de CUALQUIER comercio afiliado dentro de 50 metros,
  // en cualquier plaza comercial, ciudad o país donde HOLA RADAR opere.
  const joinUrl = `${window.location.origin}/scan`;

  const downloadPNG = () => {
    const svg = svgRef.current?.querySelector("svg");
    if (!svg) return;
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svg);
    const img = new Image();
    const svgBlob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      const size = 1024;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, size, size);
      const pad = 64;
      ctx.drawImage(img, pad, pad, size - pad * 2, size - pad * 2);
      canvas.toBlob((blob) => {
        const link = document.createElement("a");
        link.download = `qr-hola-radar.png`;
        link.href = URL.createObjectURL(blob);
        link.click();
        URL.revokeObjectURL(link.href);
      }, "image/png");
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  // Convierte el SVG del QR a un PNG dataURL para embebe en el PDF
  const qrToDataURL = () =>
    new Promise((resolve, reject) => {
      const svg = svgRef.current?.querySelector("svg");
      if (!svg) return reject(new Error("QR no encontrado"));
      const serializer = new XMLSerializer();
      const svgStr = serializer.serializeToString(svg);
      const img = new Image();
      const svgBlob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      img.onload = () => {
        const size = 1024;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, size, size);
        const pad = 32;
        ctx.drawImage(img, pad, pad, size - pad * 2, size - pad * 2);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
      img.src = url;
    });

  const downloadPDF = async () => {
    try {
      const qrData = await qrToDataURL();
      const pdf = new jsPDF({ unit: "mm", format: "a4" });
      const W = 210;
      const H = 297;

      // Fondo blanco por defecto — no hace falta pintar.
      // Marco rosa HOLA RADAR
      pdf.setDrawColor(255, 0, 122);
      pdf.setLineWidth(2);
      pdf.roundedRect(12, 12, W - 24, H - 24, 6, 6, "S");

      // Franja superior negra con logo
      pdf.setFillColor(10, 10, 10);
      pdf.roundedRect(20, 22, W - 40, 22, 4, 4, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(20);
      pdf.text("HOLA RADAR", W / 2, 36, { align: "center" });

      // Título grande
      pdf.setTextColor(255, 0, 122);
      pdf.setFontSize(48);
      pdf.text("ESCANEA Y GANA", W / 2, 68, { align: "center" });

      // Nombre del comercio
      pdf.setTextColor(20, 20, 20);
      pdf.setFontSize(20);
      pdf.setFont("helvetica", "bold");
      const bizName = (merchant.business_name || "Tu comercio").toString();
      const bizLines = pdf.splitTextToSize(bizName, W - 60);
      pdf.text(bizLines, W / 2, 82, { align: "center" });

      // Categoría opcional debajo
      if (merchant.category) {
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(120, 120, 120);
        pdf.text(String(merchant.category).toUpperCase(), W / 2, 92, { align: "center" });
      }

      // QR grande centrado
      const qrSize = 110;
      const qrX = (W - qrSize) / 2;
      const qrY = 100;
      // Sombra/fondo blanco con bordes
      pdf.setFillColor(255, 255, 255);
      pdf.setDrawColor(230, 230, 230);
      pdf.setLineWidth(0.4);
      pdf.roundedRect(qrX - 4, qrY - 4, qrSize + 8, qrSize + 8, 4, 4, "FD");
      pdf.addImage(qrData, "PNG", qrX, qrY, qrSize, qrSize);

      // Instrucciones
      pdf.setTextColor(30, 30, 30);
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      pdf.text("Apunta la cámara al código", W / 2, qrY + qrSize + 14, { align: "center" });
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(11);
      pdf.setTextColor(90, 90, 90);
      pdf.text("Se abre en tu celular — sin descargas, sin registro", W / 2, qrY + qrSize + 22, { align: "center" });

      // Callout universal
      pdf.setFillColor(255, 240, 247);
      pdf.setDrawColor(255, 0, 122);
      pdf.setLineWidth(0.3);
      pdf.roundedRect(50, qrY + qrSize + 30, W - 100, 14, 7, 7, "FD");
      pdf.setTextColor(255, 0, 122);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.text("Un QR - Todos los comercios de la red", W / 2, qrY + qrSize + 39, { align: "center" });

      // Footer
      pdf.setTextColor(150, 150, 150);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.text(joinUrl, W / 2, H - 22, { align: "center" });
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(90, 90, 90);
      pdf.text("Powered by HOLA RADAR", W / 2, H - 16, { align: "center" });

      const safeName = (merchant.business_name || "hola-radar").toString().toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40);
      pdf.save(`qr-poster-${safeName}.pdf`);
      toast.success("PDF descargado — listo para imprimir");
    } catch (e) {
      toast.error("No se pudo generar el PDF");
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const printPoster = () => {
    const w = window.open("", "_blank", "width=800,height=1000");
    if (!w) {
      toast.error("El navegador bloqueó la ventana de impresión");
      return;
    }
    const svg = svgRef.current?.querySelector("svg")?.outerHTML || "";
    const doc = w.document;
    doc.title = `QR HOLA RADAR - ${merchant.business_name}`;

    // Build poster using safe DOM APIs (no document.write / no innerHTML with unescaped data)
    const style = doc.createElement("style");
    style.textContent = `body{font-family:-apple-system,system-ui,sans-serif;margin:0;padding:48px;background:#fff;color:#000;text-align:center}.poster{max-width:640px;margin:0 auto;border:2px solid #FF007A;border-radius:32px;padding:48px 32px}h1{font-size:44px;margin:0 0 8px;color:#FF007A;letter-spacing:-1px}h2{font-size:22px;margin:0 0 32px;color:#111}.qr{background:#fff;padding:24px;border-radius:24px;display:inline-block;margin:24px 0}.qr svg{width:340px;height:340px}p{font-size:18px;line-height:1.4;color:#333;margin:8px 0}.cta{background:#0A0A0A;color:#fff;padding:16px 32px;border-radius:100px;display:inline-block;font-weight:800;margin-top:16px;font-size:20px}.brand{margin-top:32px;font-size:14px;color:#888;letter-spacing:2px;text-transform:uppercase;font-weight:800}.universal{margin-top:16px;background:#FFF0F7;color:#FF007A;padding:10px 16px;border-radius:100px;display:inline-block;font-weight:700;font-size:14px}`;
    doc.head.appendChild(style);

    const poster = doc.createElement("div");
    poster.className = "poster";
    const h1 = doc.createElement("h1"); h1.textContent = "ESCANEA Y GANA";
    const h2 = doc.createElement("h2");
    h2.textContent = `Aquí en ${merchant.business_name} · Y en toda la red HOLA RADAR`;
    const qrWrap = doc.createElement("div"); qrWrap.className = "qr";
    // Parse the QR SVG string safely via DOMParser instead of assigning to innerHTML.
    // react-qr-code produces trusted output, but DOMParser is XSS-safe by design
    // (script tags inside SVG are inert), removing an entire class of attacks.
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svg, "image/svg+xml");
    const svgNode = svgDoc.documentElement;
    if (svgNode && svgNode.nodeName.toLowerCase() === "svg") {
      qrWrap.appendChild(doc.importNode(svgNode, true));
    }
    const p1 = doc.createElement("p");
    const p1Strong = doc.createElement("strong"); p1Strong.textContent = "Apunta tu cámara al código";
    p1.appendChild(p1Strong);
    const p2 = doc.createElement("p"); p2.textContent = "Se abre la app en tu celular · Sin descargas · Sin registro";
    const universal = doc.createElement("div"); universal.className = "universal"; universal.textContent = "✨ 1 sólo QR · Todas las tiendas afiliadas";
    const cta = doc.createElement("div"); cta.className = "cta"; cta.textContent = "¡Solo escanea!";
    const brand = doc.createElement("div"); brand.className = "brand"; brand.textContent = "Powered by HOLA RADAR";

    poster.append(h1, h2, qrWrap, p1, p2, universal, cta, brand);
    doc.body.appendChild(poster);
    setTimeout(() => w.print(), 300);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      toast.success("Enlace copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <p className="text-xs uppercase tracking-widest text-neutral-500 font-bold">QR HOLA RADAR</p>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">Un solo código QR para toda la red</h1>
        <p className="text-sm text-neutral-500 mt-1">Imprímelo y pégalo en tu vitrina. Es el <strong>mismo código</strong> que usan todos los comercios afiliados: al escanearlo, el usuario podrá recibir ofertas de cualquier comercio HOLA RADAR de la ciudad mientras camine cerca — en tu local, en una plaza, en la calle, o incluso en otra ciudad.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* QR display */}
        <div ref={svgRef} className="rounded-3xl bg-white border-2 border-[#FF007A] p-8 flex flex-col items-center" data-testid="qr-display">
          <div className="flex items-center gap-2 mb-4">
            <Radar className="w-5 h-5 text-[#FF007A]" />
            <div className="font-heading font-extrabold text-lg">HOLA RADAR</div>
          </div>
          <div className="bg-white p-4 rounded-2xl">
            <QRCode
              value={joinUrl}
              size={240}
              bgColor="#FFFFFF"
              fgColor="#0A0A0A"
              level="H"
            />
          </div>
          <div className="mt-4 text-xs uppercase tracking-widest text-neutral-500 font-bold">Escanea y activa</div>
          <div className="mt-3 inline-block text-[11px] font-bold text-[#FF007A] bg-[#FFF0F7] px-3 py-1 rounded-full">
            ✨ Un QR · Toda la red
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-4">
          <div className="rounded-2xl bg-white border border-neutral-200 p-5">
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold mb-2">Enlace universal</div>
            <div className="flex gap-2 items-center">
              <code className="text-xs bg-[#FAFAFA] rounded px-2 py-1.5 flex-1 truncate" data-testid="qr-join-url">{joinUrl}</code>
              <Button onClick={copyLink} variant="outline" size="sm" className="rounded-full" data-testid="qr-copy-btn">
                <Copy className="w-3.5 h-3.5" />
              </Button>
              <a href={joinUrl} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" className="rounded-full">
                  <ExternalLink className="w-3.5 h-3.5" />
                </Button>
              </a>
            </div>
            <p className="text-[11px] text-neutral-400 mt-2">Este es el mismo enlace que verá cualquier usuario que escanee el QR — sin importar en qué comercio esté.</p>
          </div>

          <div className="rounded-2xl bg-white border border-neutral-200 p-5">
            <QrCode className="w-6 h-6 text-[#FF007A] mb-2" />
            <div className="font-heading font-extrabold text-lg">Descarga o imprime</div>
            <p className="text-sm text-neutral-500 mt-1 mb-4">Usa PNG para redes sociales o imprime el póster listo para tu vitrina.</p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={downloadPDF} className="rounded-full bg-[#FF007A] hover:bg-[#e6006e] text-white font-bold" data-testid="qr-download-pdf-btn">
                <FileText className="w-4 h-4" /> Descargar PDF
              </Button>
              <Button onClick={downloadPNG} className="rounded-full bg-black text-white font-bold" data-testid="qr-download-btn">
                <Download className="w-4 h-4" /> Descargar PNG
              </Button>
              <Button onClick={printPoster} variant="outline" className="rounded-full font-semibold" data-testid="qr-print-btn">
                <Printer className="w-4 h-4" /> Imprimir póster
              </Button>
            </div>
            <p className="text-[11px] text-neutral-500 mt-3 leading-snug">
              El <strong>PDF</strong> viene listo tamaño carta con el nombre de tu comercio arriba del QR — pégalo en la vitrina o mostrador. El PNG es útil para redes sociales.
            </p>
          </div>

          <div className="rounded-2xl bg-gradient-to-br from-[#FF007A] to-[#ff4d9e] text-white p-5">
            <div className="font-heading font-extrabold text-lg mb-2">¿Cómo funciona la red?</div>
            <ol className="text-sm space-y-1.5 text-white/90 list-decimal list-inside">
              <li>Cualquier comercio afiliado imprime este QR y lo pega en su vitrina.</li>
              <li>El usuario escanea una sola vez — desde tu local o desde cualquier otro.</li>
              <li>Su radar queda activo. Recibe ofertas al pasar cerca de <strong>cualquier</strong> comercio HOLA RADAR, incluso en otra ciudad o país.</li>
              <li>Tu comercio aparece automáticamente cuando el usuario esté a menos de 50m.</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
